/* RAPTIC model finder — instant, no-reload product filtering.
   Fetches per-model collection grids through the Section Rendering API
   (sections/raptic-model-grid.liquid) and swaps them into the page. */
class RapticModelFinder extends HTMLElement {
  connectedCallback() {
    try {
      this.models = (JSON.parse(this.querySelector('[data-raptic-models]').textContent) || []).filter(Boolean);
      this.config = JSON.parse(this.querySelector('[data-raptic-config]').textContent);
    } catch (e) {
      console.error('[RapticFinder] Invalid config JSON', e);
      return;
    }
    if (this.models.length === 0) return;

    this.pills = {
      brand: this.querySelector('[data-pills="brand"]'),
      series: this.querySelector('[data-pills="series"]'),
      model: this.querySelector('[data-pills="model"]'),
    };
    this.steps = {
      series: this.querySelector('[data-step="series"]'),
      model: this.querySelector('[data-step="model"]'),
    };
    this.grid = this.querySelector('[data-grid]');
    this.resultTitle = this.querySelector('[data-result-title]');
    this.resultCount = this.querySelector('[data-result-count]');
    this.viewAll = this.querySelector('[data-view-all]');

    this.cache = new Map();
    this.abortController = null;
    this.state = { brand: null, series: null, model: null };

    this.renderBrands();

    // Initial state: honor the configured default series/model (e.g.
    // iPhone → iPhone 17 → iPhone 17 Pro Max) so the grid is loaded and
    // never empty on page load. Falls back to the first entries.
    const preferred =
      this.models.find((m) => m.label === this.config.defaultModel) ||
      (this.config.defaultSeries && this.models.find((m) => m.series === this.config.defaultSeries)) ||
      this.models[0];
    if (preferred) this.selectBrand(preferred.brand, preferred.series || null, preferred);
  }

  /* ---------- data helpers ---------- */

  brandList() {
    const seen = [];
    this.models.forEach((m) => {
      if (!seen.includes(m.brand)) seen.push(m.brand);
    });
    return seen;
  }

  seriesList(brand) {
    const seen = [];
    this.models
      .filter((m) => m.brand === brand && m.series)
      .forEach((m) => {
        if (!seen.includes(m.series)) seen.push(m.series);
      });
    return seen;
  }

  modelList(brand, series) {
    return this.models.filter((m) => {
      if (m.brand !== brand) return false;
      if (series) return m.series === series;
      return true;
    });
  }

  /* ---------- rendering ---------- */

  makePill(label, extraClass) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'raptic-pill' + (extraClass ? ' ' + extraClass : '');
    btn.textContent = label;
    return btn;
  }

  markActive(container, activeBtn) {
    container.querySelectorAll('.raptic-pill').forEach((b) => {
      b.classList.toggle('is-active', b === activeBtn);
      b.setAttribute('aria-pressed', b === activeBtn ? 'true' : 'false');
    });
  }

  revealStep(stepEl) {
    if (!stepEl.hidden) return;
    stepEl.hidden = false;
    stepEl.classList.add('is-entering');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => stepEl.classList.remove('is-entering'));
    });
  }

  renderBrands() {
    this.pills.brand.innerHTML = '';
    this.brandList().forEach((brand) => {
      const label = (this.config.brandNames || {})[brand] || brand;
      const btn = this.makePill(label, 'raptic-pill--brand');
      btn.addEventListener('click', () => this.selectBrand(brand));
      btn.dataset.value = brand;
      this.pills.brand.appendChild(btn);
    });
  }

  selectBrand(brand, preferredSeries, preferredModel) {
    this.state.brand = brand;
    this.markActive(this.pills.brand, this.pills.brand.querySelector('[data-value="' + CSS.escape(brand) + '"]'));

    const series = this.seriesList(brand);
    if (series.length > 0) {
      this.renderSeries(series);
      this.revealStep(this.steps.series);
      const chosenSeries = preferredSeries && series.includes(preferredSeries) ? preferredSeries : series[0];
      this.selectSeries(chosenSeries, preferredModel);
    } else {
      this.steps.series.hidden = true;
      this.state.series = null;
      const models = this.modelList(brand, null);
      this.renderModels(models);
      this.revealStep(this.steps.model);
      const chosen = preferredModel && models.includes(preferredModel) ? preferredModel : models[0];
      if (chosen) this.selectModel(chosen);
    }
  }

  renderSeries(seriesArr) {
    this.pills.series.innerHTML = '';
    seriesArr.forEach((s) => {
      const btn = this.makePill(s);
      btn.dataset.value = s;
      btn.addEventListener('click', () => this.selectSeries(s));
      this.pills.series.appendChild(btn);
    });
  }

  selectSeries(series, preferredModel) {
    this.state.series = series;
    this.markActive(this.pills.series, this.pills.series.querySelector('[data-value="' + CSS.escape(series) + '"]'));

    const models = this.modelList(this.state.brand, series);
    this.renderModels(models);
    this.revealStep(this.steps.model);
    const chosen = preferredModel && models.includes(preferredModel) ? preferredModel : models[0];
    if (chosen) this.selectModel(chosen);
  }

  renderModels(models) {
    this.pills.model.innerHTML = '';
    models.forEach((m) => {
      const btn = this.makePill(m.label);
      btn.dataset.value = m.url;
      btn.addEventListener('click', () => this.selectModel(m));
      this.pills.model.appendChild(btn);
    });
  }

  selectModel(model) {
    this.state.model = model;
    this.markActive(this.pills.model, this.pills.model.querySelector('[data-value="' + CSS.escape(model.url) + '"]'));
    this.loadGrid(model);
  }

  /* ---------- fetching ---------- */

  async loadGrid(model) {
    const url = model.url + '?section_id=' + this.config.gridSection;

    this.grid.classList.add('is-loading');
    this.grid.setAttribute('aria-busy', 'true');

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      let html = this.cache.get(url);
      if (!html) {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        html = await response.text();
        this.cache.set(url, html);
      }

      // The user may have clicked another model while this request ran.
      if (this.state.model !== model) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector('.raptic-grid-fragment');
      if (!fragment) throw new Error('Grid fragment missing in response');

      this.grid.innerHTML = '';
      this.grid.appendChild(fragment);

      this.updateMeta(model, fragment);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[RapticFinder] Failed to load grid', err);
      this.grid.innerHTML =
        '<div class="raptic-finder__empty">' + (model.label || '') + ' — please try again.</div>';
    } finally {
      if (this.state.model === model) {
        this.grid.classList.remove('is-loading');
        this.grid.setAttribute('aria-busy', 'false');
      }
    }
  }

  updateMeta(model, fragment) {
    const count = parseInt(fragment.dataset.count, 10) || 0;
    const template = this.config.productCountTemplate || '{count}';

    this.resultTitle.textContent = model.label;
    this.resultCount.textContent = ' · ' + template.replace('{count}', count);

    if (this.viewAll) {
      this.viewAll.href = fragment.dataset.url || model.url;
      // The grid shows up to 50 products (Shopify's pagination maximum);
      // only offer "view all" when the collection has more than that.
      this.viewAll.hidden = count <= 50;
    }
  }
}

if (!customElements.get('raptic-model-finder')) {
  customElements.define('raptic-model-finder', RapticModelFinder);
}
